import { HugeiconsIcon } from "@hugeicons/react";
import { AntennaIcon, Briefcase01Icon, ShoppingCart01Icon, Target01Icon } from "@hugeicons-pro/core-bulk-rounded";
import type { IconSvgElement } from "@hugeicons/react";

import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";

import { PageHeader } from "@/components/layout/page-header";
import { GlobalFilterBar } from "@/components/filters/global-filter-bar";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FadeIn } from "@/components/ui/motion";
import { useFilters } from "@/lib/filters";
import { useReference, brandById } from "@/lib/use-reference";
import { useMoney } from "@/lib/use-money";
import { useCollaborations } from "@/lib/use-metrics";
import { useExactReach } from "@/lib/use-reach";
import { useBenchmarkEditor } from "@/lib/use-benchmark-editor";
import { getTotals, type RollupQuery, type Kpis } from "@/lib/api/metrics";
import { collabStats } from "@/lib/api/collabs";
import { scaleTarget } from "@/lib/benchmarks";
import { BenchmarkCell } from "@/components/dashboard/benchmark-cell";
import type { ActivityType, Benchmark, MetricDef } from "@/lib/api/types";

const ACTIVITY_LABEL: Record<ActivityType, string> = {
  ecom: "E-commerce",
  awareness: "Awareness",
  job_ads: "Job ads",
};
const ACTIVITY_ICON: Record<ActivityType, IconSvgElement> = {
  ecom: ShoppingCart01Icon,
  awareness: AntennaIcon,
  job_ads: Briefcase01Icon,
};

/** Which metrics are worth setting a goal on, per activity. Inputs you buy
 *  (spend, impressions, clicks) and metrics without a data source are left out —
 *  a goal you cannot measure is worse than no goal. */
const GOAL_METRICS: Record<ActivityType, string[]> = {
  ecom: ["revenue", "roas", "mer", "aov", "orders", "conversion_rate",
         "new_customers", "cpa_new_customer", "net_revenue_growth", "paid_engagement_rate"],
  awareness: ["reached_accounts", "organic_engagement_rate", "follower_growth",
              "content_pieces", "video_views", "influencer_collabs", "website_visitors"],
  job_ads: ["applications", "cost_per_application"],
};

const comboKey = (q: RollupQuery) => `${q.activity}|${q.region}|${q.brand}|${q.channel}`;

export function Benchmarks() {
  const f = useFilters();
  const m = useMoney();
  const { benchmarks, metrics, brands, isLoading } = useReference();
  const { saveById, saveByKey } = useBenchmarkEditor();
  const collabs = useCollaborations();
  // Reach is unique people per platform and period (asked of the platforms);
  // the rollup's reach is a sum of days and is not used for it.
  const reach = useExactReach({ activity: "awareness" });

  // Distinct (activity, region, brand) rollups needed to evaluate every goal.
  // Every listed metric needs its activity's rollup, whether or not a target is
  // stored yet — the realised figure is shown either way.
  const combos = useMemo(() => {
    const map = new Map<string, RollupQuery>();
    const add = (activity: ActivityType, region: string | null, brand: string | null) => {
      const q: RollupQuery = {
        from: f.fromDate,
        to: f.toDate,
        activity,
        region: (region ?? f.region) as RollupQuery["region"],
        brand: (brand ?? f.brand) as RollupQuery["brand"],
        channel: f.channel,
      };
      map.set(comboKey(q), q);
    };
    for (const a of ["ecom", "awareness", "job_ads"] as ActivityType[]) add(a, null, null);
    for (const b of benchmarks) add(b.activity_type, b.region, b.brand_id);
    return [...map.values()];
  }, [benchmarks, f.fromDate, f.toDate, f.region, f.brand, f.channel]);

  const results = useQueries({
    queries: combos.map((q) => ({
      queryKey: ["bench", q.from, q.to, q.activity, q.region, q.brand, q.channel],
      queryFn: () => getTotals(q),
    })),
  });

  const kpiByCombo = new Map<string, Kpis>();
  combos.forEach((q, i) => {
    const data = results[i]?.data;
    if (data) kpiByCombo.set(comboKey(q), data.kpis);
  });

  const loading = isLoading || results.some((r) => r.isPending);
  const activeCollabs = collabs.data ? collabStats(collabs.data).active : null;

  const groups: ActivityType[] = ["ecom", "awareness", "job_ads"];

  function actualFor(metricKey: string, q: RollupQuery): number | null {
    if (metricKey === "influencer_collabs") return activeCollabs;
    if (q.activity === "awareness" && (metricKey === "reached_accounts" || metricKey === "organic_engagement_rate")) {
      const v = reach.scoped(q.region, q.brand);
      return (metricKey === "reached_accounts" ? v?.reach : v?.er) ?? null;
    }
    const kpis = kpiByCombo.get(comboKey(q));
    if (!kpis) return null;
    return (kpis as unknown as Record<string, number | null>)[metricKey] ?? null;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Benchmarks"
        icon={Target01Icon}
        title="Benchmarks"
        description="Realisatie tegenover doel — te segmenteren per regio en per merk."
        tabs={<GlobalFilterBar />}
      />

      {loading ? (
        <div className="space-y-6">
          {Array.from({ length: 2 }).map((_, i) => (
            <Card key={i} className="overflow-hidden p-0">
              <div className="band-header flex items-center gap-2.5 px-5 py-4">
                <Skeleton className="h-8 w-8 rounded-lg" />
                <Skeleton className="h-4 w-28 rounded" />
              </div>
              <div>
                <div className="grid grid-cols-1 gap-px overflow-hidden rounded-t-xl bg-border sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 3 }).map((_, j) => (
                  <div key={j} className="bg-card p-5">
                    <Skeleton className="h-16 w-full rounded" />
                  </div>
                ))}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        groups.map((activity, gi) => {
          // Every goal-worthy metric for this activity, plus any stored target
          // that is scoped to a specific region or brand.
          const base = GOAL_METRICS[activity]
            .map((key) => {
              const def = metrics.find((mm) => mm.key === key);
              return def ? { def, bench: benchmarks.find((b) => b.metric_key === key && b.activity_type === activity && !b.region && !b.brand_id) ?? null } : null;
            })
            .filter((x): x is { def: MetricDef; bench: Benchmark | null } => x !== null);
          const scoped = benchmarks
            .filter((b) => b.activity_type === activity && (b.region || b.brand_id))
            .map((b) => {
              const def = metrics.find((mm) => mm.key === b.metric_key);
              return def ? { def, bench: b } : null;
            })
            .filter((x): x is { def: MetricDef; bench: Benchmark } => x !== null);
          const items = [...base, ...scoped];
          if (items.length === 0) return null;
          const ActIcon = ACTIVITY_ICON[activity];
          const fillers = (3 - (items.length % 3)) % 3;
          const setCount = items.filter((x) => x.bench).length;
          return (
            <FadeIn key={activity} delay={gi * 0.05}>
              <Card className="overflow-hidden p-0">
                <div className="band-header flex items-center gap-2.5 px-5 py-4">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/15">
                    <HugeiconsIcon icon={ActIcon} size={18} />
                  </span>
                  <h3 className="font-heading text-base font-semibold">{ACTIVITY_LABEL[activity]}</h3>
                  <Badge variant="outline" size="sm" className="ml-auto">{setCount} / {items.length}</Badge>
                </div>
                <div>
                <div className="grid grid-cols-1 gap-px overflow-hidden rounded-t-xl bg-border sm:grid-cols-2 lg:grid-cols-3">
                    {items.map(({ def, bench: b }) => {
                      const q: RollupQuery = {
                        from: f.fromDate, to: f.toDate,
                        activity,
                        region: (b?.region ?? f.region) as RollupQuery["region"],
                        brand: (b?.brand_id ?? f.brand) as RollupQuery["brand"],
                        channel: f.channel,
                      };
                      const effTarget = b ? scaleTarget(b.target_value, b.metric_key, b.period, f.windowDays) : null;
                      const scopeBits = b
                        ? [
                            b.region ? b.region : null,
                            b.brand_id ? brandById(brands, b.brand_id)?.name : null,
                            b.note || null,
                            effTarget !== b.target_value ? `doel geschaald naar ${f.windowDays} dagen` : null,
                          ].filter(Boolean)
                        : [];
                      return (
                        <BenchmarkCell
                          key={b?.id ?? `${activity}-${def.key}`}
                          label={def.label}
                          actual={actualFor(def.key, q)}
                          target={effTarget}
                          editValue={b?.target_value}
                          unit={def.unit}
                          higherIsBetter={def.higher_is_better}
                          money={m.fmt}
                          note={b ? scopeBits.join(" · ") || `Doel per ${b.period === "monthly" ? "maand" : b.period}` : "Nog geen doel bepaald"}
                          onSaveTarget={(v) =>
                            b
                              ? saveById(b.id, v)
                              : saveByKey({ metric_key: def.key, activity_type: activity, region: null, brand_id: null }, v)
                          }
                        />
                      );
                    })}
                    {Array.from({ length: fillers }).map((_, i) => (
                      <div key={`filler-${i}`} className="hidden bg-card lg:block" />
                    ))}
                </div>
                </div>
              </Card>
            </FadeIn>
          );
        })
      )}
    </div>
  );
}
